import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconCircleCheck, IconShoppingCart, IconSparkles, IconBrandWhatsapp, IconCoins } from "@tabler/icons-react";
import paymentService from "../../services/paymentService";
import { useAuthStore } from "../../store/authStore";
import Logo from "../../components/common/Logo";

const REDIRECT_DELAY_MS = 4000;

// Support contact (same WhatsApp line used across the app — see WhatsappFloat / PrivateLayout).
const SUPPORT_WA = "8801700559595";
const SUPPORT_WA_DISPLAY = "01700-559595";

// ASCII → Bangla numerals for the receipt-style details.
const toBn = (s) => String(s).replace(/[0-9]/g, (d) => "০১২৩৪৫৬৭৮৯"[+d]);
const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("bn-BD", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return new Date(iso).toLocaleString();
    }
};

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get("orderId");
    const transactionId = searchParams.get("transaction_id");
    const gateway = searchParams.get("gateway"); // 'ssl' for SSLCommerz, absent for Moneybag
    const { getCurrentUser } = useAuthStore();
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(true);

    // If SSLCommerz's popup landed us here inside its iframe, break out to the top window
    // so the credit refresh + full success page show in the user's main tab (not the modal).
    useEffect(() => {
        if (window.top && window.top !== window.self) {
            try { window.top.location.href = window.location.href; } catch { /* cross-origin: ignore */ }
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 8;

        const settle = async () => {
            try {
                const verified = gateway === "ssl"
                    ? await paymentService.verifyPaymentSSL(orderId)
                    : await paymentService.verifyPayment(orderId, transactionId);
                if (cancelled) return;
                if (verified.status === "SUCCESS") {
                    const status = await paymentService.getStatus(orderId);
                    setPayment(status);
                    setLoading(false);
                    await getCurrentUser();
                    return;
                }
            } catch {
                // verify failed — fall through to polling
            }

            const poll = async () => {
                try {
                    const data = await paymentService.getStatus(orderId);
                    if (cancelled) return;

                    if (data.status === "SUCCESS") {
                        setPayment(data);
                        setLoading(false);
                        await getCurrentUser();
                        return;
                    }

                    if ((data.status === "PENDING" || data.status === "CANCELLED") && attempts < maxAttempts) {
                        attempts += 1;
                        setTimeout(poll, 2000);
                        return;
                    }

                    setPayment(data);
                    setLoading(false);
                } catch (err) {
                    if (!cancelled) setLoading(false);
                }
            };

            poll();
        };

        if (orderId) setTimeout(settle, 1500);
        else setLoading(false);

        return () => { cancelled = true; };
    }, [orderId, transactionId, gateway, getCurrentUser]);

    useEffect(() => {
        if (loading) return;
        const timer = setTimeout(() => navigate("/settings?tab=credits"), REDIRECT_DELAY_MS);
        return () => clearTimeout(timer);
    }, [loading, navigate]);

    const isSuccess = payment?.status === "SUCCESS";

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-zinc-50 dark:bg-zinc-950">
            <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 shadow-lg p-8">
                <div className="flex flex-col items-center gap-4 text-center">
                    {/* Brand logo — shown in every state */}
                    <Logo w={150} />

                    {loading ? (
                        <>
                            {/* Spinner */}
                            <div className="mt-2 w-16 h-16 border-4 border-zinc-200 border-t-blue-600 rounded-full animate-spin" />
                            <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">পেমেন্ট যাচাই করা হচ্ছে...</h3>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                অনুগ্রহ করে কয়েক সেকেন্ড অপেক্ষা করুন।
                            </p>
                        </>
                    ) : (
                        <>
                            {/* Success icon */}
                            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <IconCircleCheck size={48} className="text-green-600 dark:text-green-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-green-600 dark:text-green-400">পেমেন্ট সফল!</h2>

                            {/* Thank-you */}
                            <p className="text-sm text-zinc-600 dark:text-zinc-300 -mt-1">
                                সহজ কাজ থেকে ক্রেডিট ক্রয় করার জন্য আপনাকে ধন্যবাদ 
                            </p>

                            {isSuccess ? (
                                <>
                                    <p className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                                        {toBn(payment.credits)} ক্রেডিট আপনার অ্যাকাউন্টে যোগ হয়েছে
                                    </p>

                                    {/* Order details — receipt-style summary */}
                                    <div className="w-full mt-1 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 divide-y divide-zinc-200 dark:divide-zinc-700 text-sm">
                                        <div className="flex items-center justify-between px-4 py-2.5">
                                            <span className="text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5"><IconCoins size={15} /> ক্রেডিট</span>
                                            <span className="font-semibold text-zinc-800 dark:text-zinc-100">{toBn(payment.credits)}</span>
                                        </div>
                                        <div className="flex items-center justify-between px-4 py-2.5">
                                            <span className="text-zinc-500 dark:text-zinc-400">পরিমাণ</span>
                                            <span className="font-semibold text-zinc-800 dark:text-zinc-100">৳{toBn(payment.amount)}</span>
                                        </div>
                                        {payment.orderId && (
                                            <div className="flex items-center justify-between px-4 py-2.5 gap-3">
                                                <span className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">অর্ডার আইডি</span>
                                                <span className="font-mono text-xs text-zinc-700 dark:text-zinc-200 truncate">{payment.orderId}</span>
                                            </div>
                                        )}
                                        {payment.createdAt && (
                                            <div className="flex items-center justify-between px-4 py-2.5">
                                                <span className="text-zinc-500 dark:text-zinc-400">তারিখ</span>
                                                <span className="text-zinc-700 dark:text-zinc-200">{fmtDate(payment.createdAt)}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* What's next */}
                                    <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
                                        আপনি ক্রেডিট পেয়ে গেছেন — এখন কাজ শুরু করতে পারেন।
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-base font-semibold text-orange-600 dark:text-orange-400">
                                        পেমেন্ট প্রসেস হচ্ছে
                                    </p>
                                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                        গেটওয়ে নিশ্চিতকরণের জন্য অপেক্ষা করছে। কিছু সময়ের মধ্যে
                                        ক্রেডিট আপনার অ্যাকাউন্টে যোগ হবে।
                                    </p>
                                </>
                            )}

                            <p className="text-xs text-zinc-400 dark:text-zinc-500">
                                {toBn(Math.ceil(REDIRECT_DELAY_MS / 1000))} সেকেন্ডে সহজকাজে নিয়ে যাওয়া হবে...
                            </p>

                            {/* Actions */}
                            <div className="w-full flex flex-col sm:flex-row gap-2.5 mt-1">
                                {isSuccess && (
                                    <button
                                        onClick={() => navigate("/")}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-95 text-white rounded-lg transition-opacity"
                                    >
                                        <IconSparkles size={16} />
                                        কাজ শুরু করুন
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate("/settings?tab=credits")}
                                    className={`flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                                        isSuccess
                                            ? "flex-1 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                                            : "w-full bg-blue-600 hover:bg-blue-700 text-white"
                                    }`}
                                >
                                    <IconShoppingCart size={16} />
                                    ক্রেডিট দেখুন
                                </button>
                            </div>

                            {/* Support / contact */}
                            <div className="w-full mt-2 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                    কোনো সমস্যা হলে বা ক্রেডিট যোগ না হলে যোগাযোগ করুন
                                </p>
                                <a
                                    href={`https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(
                                        `আসসালামু আলাইকুম, আমার পেমেন্ট (অর্ডার আইডি: ${orderId || ""}) নিয়ে সাহায্য প্রয়োজন।`
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 mt-1.5 text-sm font-semibold text-green-600 dark:text-green-400 hover:underline"
                                >
                                    <IconBrandWhatsapp size={16} /> {SUPPORT_WA_DISPLAY}
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
